import { Box, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import { Button, ButtonValid, Modal, Text } from '@feathr/components';
import { useFlags } from '@feathr/extender/state';

import EmailActivityTab from './EmailActivityTab';
import ExportDetailsStep from './ExportDetailsStep';
import { useGroupExportModal } from './GroupExportModal.hooks';
import type { TGroupExportType } from './GroupExportModal.types';
import type {
  IEmailActivityExportPayload,
  IOtherActivitiesExportPayload,
} from './GroupExportModal.utils';
import OtherActivitiesTab from './OtherActivitiesTab';
import PersonTab from './PersonTab';
import SimpleExportForm from './SimpleExportForm';

import * as styles from './GroupExportModal.css';

export interface IGroupExportModalProps {
  opened: boolean;
  onClose: () => void;
  segment: Segment;
  /** Overrides the live export call; the modal wires the real one when omitted. */
  onExportEmailActivity?: (payload: IEmailActivityExportPayload) => Promise<void>;
  onExportOtherActivities?: (payload: IOtherActivitiesExportPayload) => Promise<void>;
  /** Overrides the "View export" action the success toast renders by default. */
  successToastAction?: IToastAction;
}

function GroupExportModal({
  opened,
  onClose,
  segment,
  onExportEmailActivity,
  onExportOtherActivities,
  successToastAction,
}: Readonly<IGroupExportModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { showEmailActivityExport } = useFlags();
  const {
    step,
    exportType,
    detailsStep,
    detailsStepErrors,
    isLoading,
    isSubmitDisabled,
    personTab,
    emailActivityTab,
    otherActivitiesTab,
    simpleExportForm,
    handleNext,
    handleBack,
    handleSubmit,
    handleClose,
  } = useGroupExportModal({
    segment,
    onClose,
    onExportEmailActivity,
    onExportOtherActivities,
    successToastAction,
  });

  // Block dismissal (Cancel, overlay click, Esc) while a submit is in flight so
  // the toast never lands on an already-closed modal.
  const handleGuardedClose = useCallback((): void => {
    if (isLoading) {
      return;
    }
    handleClose();
  }, [handleClose, isLoading]);

  const cancelButton = (
    <Button
      disabled={isLoading}
      key={'cancel'}
      name={'group-export-cancel'}
      onClick={handleGuardedClose}
    >
      {t('Cancel')}
    </Button>
  );

  // Named because a submitting Button swaps its label for a spinner, which leaves
  // it with no accessible name to select by while a submit is in flight.
  const submitButton = (
    <Button
      disabled={isSubmitDisabled}
      isLoading={isLoading}
      key={'confirm'}
      name={'group-export-submit'}
      onClick={handleSubmit}
      type={'primary'}
    >
      {t('Export')}
    </Button>
  );

  const wizardActions =
    step === 'details'
      ? [
          cancelButton,
          <ButtonValid
            errors={detailsStepErrors}
            key={'next'}
            name={'group-export-next'}
            onClick={handleNext}
            type={'primary'}
          >
            {t('Next')}
          </ButtonValid>,
        ]
      : [
          <Button disabled={isLoading} key={'back'} name={'group-export-back'} onClick={handleBack}>
            {t('Back')}
          </Button>,
          submitButton,
        ];

  const configureStepByType: Record<TGroupExportType, ReactNode> = {
    person: <PersonTab state={personTab.state} />,
    emailActivity: <EmailActivityTab state={emailActivityTab.state} />,
    otherActivities: <OtherActivitiesTab state={otherActivitiesTab.state} />,
  };

  return (
    <Modal
      bodyClassName={styles.body}
      description={t(
        'Export the data in the chosen group as a .csv file. When your export file is ready, we will send you an email with a download link.',
      )}
      leftActions={
        showEmailActivityExport
          ? [
              <Text key={'step-indicator'} variant={'smallRegular'}>
                {t('Step {{current}} of {{total}}', {
                  current: step === 'details' ? 1 : 2,
                  total: 2,
                })}
              </Text>,
            ]
          : undefined
      }
      onClose={handleGuardedClose}
      opened={opened}
      rightActions={showEmailActivityExport ? wizardActions : [cancelButton, submitButton]}
      title={t('Export Group')}
    >
      <Box mt={theme.spacing['3']}>
        {!showEmailActivityExport && <SimpleExportForm state={simpleExportForm.state} />}
        {showEmailActivityExport && step === 'details' && <ExportDetailsStep state={detailsStep} />}
        {showEmailActivityExport && step === 'configure' && configureStepByType[exportType]}
      </Box>
    </Modal>
  );
}

export default observer(GroupExportModal);
